import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  api,
  type AvatarOptions,
  type DashboardResponse,
  type FairnessResponse,
  type FeedPull,
  type HistoryOpen,
  type LeaderboardEntry,
  type LedgerEntry,
  type MarketResponse,
  type OpenResult,
  type PackDetail,
  type PackSummary,
  type PublicProfile,
  type ReferralSummary,
  type SiteStats,
  type User,
  type UsernameCheck,
  type VaultResponse,
  type WalletResponse,
  type WithdrawalRow,
  type VerifyResponse,
} from '../lib/api'
import { meKey } from './useAuth'

export const keys = {
  packs: ['packs'] as const,
  pack: (id: string) => ['packs', id] as const,
  market: ['market'] as const,
  vault: ['vault'] as const,
  feed: ['feed'] as const,
  leaderboard: (window: string) => ['leaderboard', window] as const,
  fairness: ['fairness'] as const,
  history: ['history'] as const,
  ledger: ['ledger'] as const,
  stats: ['stats'] as const,
  verify: (id: string) => ['verify', id] as const,
  dashboard: ['dashboard'] as const,
  profile: (username: string) => ['profile', username] as const,
  avatarOptions: ['avatar-options'] as const,
  wallet: ['wallet'] as const,
  referral: ['referral'] as const,
  usernameCheck: (name: string) => ['username-check', name] as const,
}

/** The catalogue is a build-time constant on the server; cache it hard. */
export const usePacks = () =>
  useQuery({
    queryKey: keys.packs,
    queryFn: () => api.get<{ packs: PackSummary[] }>('/packs').then((r) => r.packs),
    staleTime: 10 * 60_000,
  })

export const usePack = (id: string | undefined) =>
  useQuery({
    queryKey: keys.pack(id ?? ''),
    queryFn: () => api.get<PackDetail>(`/packs/${id}`),
    enabled: Boolean(id),
    staleTime: 60_000,
    // Dragging the card slider changes the pack id on every step. Without this
    // the odds table and the distribution blank out between steps, which reads
    // as the page breaking rather than as a number being recalculated.
    placeholderData: keepPreviousData,
  })

export const useMarket = () =>
  useQuery({
    queryKey: keys.market,
    queryFn: () => api.get<MarketResponse>('/market'),
    staleTime: 60_000,
  })

export const useVault = (enabled = true) =>
  useQuery({
    queryKey: keys.vault,
    queryFn: () => api.get<VaultResponse>('/vault'),
    enabled,
    staleTime: 15_000,
  })

/** The home-page ticker. Polls, because the whole point is that it is live. */
export const useFeed = (limit = 30) =>
  useQuery({
    queryKey: [...keys.feed, limit],
    queryFn: () => api.get<{ pulls: FeedPull[] }>(`/feed?limit=${limit}`).then((r) => r.pulls),
    refetchInterval: 20_000,
    staleTime: 10_000,
  })

export const useLeaderboard = (window: 'day' | 'week' | 'all' = 'week') =>
  useQuery({
    queryKey: keys.leaderboard(window),
    queryFn: () =>
      api
        .get<{ entries: LeaderboardEntry[] }>(`/leaderboard?window=${window}`)
        .then((r) => r.entries),
    staleTime: 30_000,
  })

export const useFairness = (enabled = true) =>
  useQuery({
    queryKey: keys.fairness,
    queryFn: () => api.get<FairnessResponse>('/fairness'),
    enabled,
    staleTime: 15_000,
  })

export const useHistory = (enabled = true) =>
  useQuery({
    queryKey: keys.history,
    queryFn: () => api.get<{ opens: HistoryOpen[] }>('/history?limit=50').then((r) => r.opens),
    enabled,
    staleTime: 15_000,
  })

export const useLedger = (enabled = true) =>
  useQuery({
    queryKey: keys.ledger,
    queryFn: () => api.get<{ entries: LedgerEntry[] }>('/ledger').then((r) => r.entries),
    enabled,
    staleTime: 15_000,
  })

export const useSiteStats = () =>
  useQuery({
    queryKey: keys.stats,
    queryFn: () => api.get<SiteStats>('/stats'),
    staleTime: 60_000,
  })

export const useVerify = (openId: string | undefined) =>
  useQuery({
    queryKey: keys.verify(openId ?? ''),
    queryFn: () => api.get<VerifyResponse>(`/fairness/verify/${openId}`),
    enabled: Boolean(openId),
    retry: false,
  })

/* ------------------------------------------------------------------ *
 * Profiles
 * ------------------------------------------------------------------ */

/** The signed-in account's own dashboard. Never cached across sessions. */
export const useDashboard = (enabled = true) =>
  useQuery({
    queryKey: keys.dashboard,
    queryFn: () => api.get<DashboardResponse>('/me/dashboard'),
    enabled,
    staleTime: 15_000,
  })

/** Somebody else's profile. 404s for a handle that is private or absent. */
export const useProfile = (username: string | undefined) =>
  useQuery({
    queryKey: keys.profile(username ?? ''),
    queryFn: () => api.get<PublicProfile>(`/users/${username}`),
    enabled: Boolean(username),
    retry: false,
  })

export const useAvatarOptions = (enabled = true) =>
  useQuery({
    queryKey: keys.avatarOptions,
    queryFn: () => api.get<AvatarOptions>('/me/avatar-options'),
    enabled,
    staleTime: 60_000,
  })

/**
 * Availability for the handle currently typed into the settings form.
 *
 * Debouncing is the caller's job — this only fires for names long enough to be
 * legal, so the endpoint is not hit on every keystroke of `a`, `ab`, `abc`.
 */
export const useUsernameCheck = (name: string) =>
  useQuery({
    queryKey: keys.usernameCheck(name),
    queryFn: () => api.get<UsernameCheck>(`/profile/username-available?u=${encodeURIComponent(name)}`),
    enabled: name.length >= 3,
    staleTime: 30_000,
    retry: false,
  })

/* ------------------------------------------------------------------ *
 * Wallet
 * ------------------------------------------------------------------ */

/**
 * Deposit address, limits and both histories.
 *
 * Polled while the page is open: a deposit is credited by a worker watching the
 * chain, so the only way the page learns about one is by asking again.
 */
export const useWallet = (enabled = true) =>
  useQuery({
    queryKey: keys.wallet,
    queryFn: () => api.get<WalletResponse>('/wallet'),
    enabled,
    refetchInterval: 20_000,
    staleTime: 0,
  })

/* ------------------------------------------------------------------ *
 * Referrals
 * ------------------------------------------------------------------ */

export const useReferral = (enabled = true) =>
  useQuery({
    queryKey: keys.referral,
    queryFn: () => api.get<ReferralSummary>('/me/referral'),
    enabled,
    staleTime: 15_000,
  })

/* ------------------------------------------------------------------ *
 * Mutations
 * ------------------------------------------------------------------ */

export function useGenerateReferralCode() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<{ code: string }>('/me/referral/code'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.referral }),
  })
}

export function useRedeemReferralCode() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (code: string) =>
      api.post<{ bonusCents: number; balanceCents: number; referrerHandle: string | null }>(
        '/me/referral/redeem',
        { code },
      ),
    onSuccess: (result) => {
      // The bonus lands immediately, so the header balance has to move with it.
      queryClient.setQueryData(meKey, (previous: unknown) =>
        previous && typeof previous === 'object'
          ? { ...previous, balanceCents: result.balanceCents }
          : previous,
      )
      queryClient.invalidateQueries({ queryKey: keys.referral })
      queryClient.invalidateQueries({ queryKey: keys.ledger })
      queryClient.invalidateQueries({ queryKey: keys.dashboard })
    },
  })
}

function patchBalance(queryClient: ReturnType<typeof useQueryClient>, balanceCents: number) {
  queryClient.setQueryData(meKey, (previous: unknown) =>
    previous && typeof previous === 'object' ? { ...previous, balanceCents } : previous,
  )
  queryClient.invalidateQueries({ queryKey: keys.wallet })
  queryClient.invalidateQueries({ queryKey: keys.dashboard })
  queryClient.invalidateQueries({ queryKey: keys.ledger })
}

export function useRequestWithdrawal() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (amountCents: number) =>
      api.post<{ withdrawal: WithdrawalRow; balanceCents: number }>('/wallet/withdraw', {
        amountCents,
      }),
    onSuccess: (result) => patchBalance(queryClient, result.balanceCents),
  })
}

export function useCancelWithdrawal() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ ok: true; balanceCents: number }>(`/wallet/withdrawals/${id}/cancel`),
    onSuccess: (result) => patchBalance(queryClient, result.balanceCents),
  })
}

export interface ProfilePatch {
  username?: string
  bio?: string | null
  avatarKind?: User['avatarKind']
  avatarRef?: string | null
  profilePublic?: boolean
}

export function useUpdateProfile() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (patch: ProfilePatch) => api.patch<{ user: User }>('/me/profile', patch),
    onSuccess: (result) => {
      queryClient.setQueryData(meKey, result.user)
      // The handle and the avatar are stamped onto the ticker and the board, so
      // both have to be refetched or the page keeps showing the old identity
      // next to results that are already yours.
      queryClient.invalidateQueries({ queryKey: keys.dashboard })
      queryClient.invalidateQueries({ queryKey: keys.feed })
      queryClient.invalidateQueries({ queryKey: ['leaderboard'] })
    },
  })
}

export function useOpenPack() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (packId: string) => api.post<OpenResult>(`/packs/${packId}/open`),
    onSuccess: (result) => {
      // The balance comes back with the result, so patch it straight in rather
      // than refetching — the reveal screen should not flicker a stale number.
      queryClient.setQueryData(meKey, (previous: unknown) =>
        previous && typeof previous === 'object'
          ? { ...previous, balanceCents: result.balanceCents }
          : previous,
      )
      queryClient.invalidateQueries({ queryKey: keys.vault })
      queryClient.invalidateQueries({ queryKey: keys.feed })
      queryClient.invalidateQueries({ queryKey: keys.history })
      queryClient.invalidateQueries({ queryKey: keys.fairness })
      queryClient.invalidateQueries({ queryKey: keys.ledger })
    },
  })
}

export function useSellPositions() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (positionIds: string[]) =>
      api.post<{ soldCount: number; proceedsCents: number; balanceCents: number }>('/vault/sell', {
        positionIds,
      }),
    onSuccess: (result) => {
      queryClient.setQueryData(meKey, (previous: unknown) =>
        previous && typeof previous === 'object'
          ? { ...previous, balanceCents: result.balanceCents }
          : previous,
      )
      queryClient.invalidateQueries({ queryKey: keys.vault })
      queryClient.invalidateQueries({ queryKey: keys.ledger })
    },
  })
}

export function useSellOpen() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (openId: string) =>
      api.post<{ soldCount: number; proceedsCents: number; balanceCents: number }>(
        `/vault/sell-open/${openId}`,
      ),
    onSuccess: (result) => {
      queryClient.setQueryData(meKey, (previous: unknown) =>
        previous && typeof previous === 'object'
          ? { ...previous, balanceCents: result.balanceCents }
          : previous,
      )
      queryClient.invalidateQueries({ queryKey: keys.vault })
      queryClient.invalidateQueries({ queryKey: keys.ledger })
    },
  })
}

export function useRotateSeeds() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<FairnessResponse>('/fairness/rotate'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.fairness })
      queryClient.invalidateQueries({ queryKey: keys.history })
    },
  })
}

export function useSetClientSeed() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (clientSeed: string) =>
      api.post<FairnessResponse>('/fairness/client-seed', { clientSeed }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.fairness })
      queryClient.invalidateQueries({ queryKey: keys.history })
    },
  })
}
